import {priority, wcfclient} from '@priority-software/priority-proto-api/lib';
import {noop} from '@priority-software/priority-style-react';
import {getAdditionalProps} from '../../../services/proto/core/loginProps';
import {callServer} from '../../../services/proto/core/server';
import {ServerMethods} from '../../../services/proto/core/constants';
import {updateAutomationRules} from '../../../_legacy/Form/services/formService';
import {handleFormFatalError, isCurrentEntBPM} from '../../../_shared/GWTBridge/invoke/genPage';
import {setNFormFatalError} from '../../../_shared/GWTBridge/invoke/netForm';
import {formsStore} from '../../../stores/Form';
import {setFormSystemConstants} from '../../Form/actions/form';
import {getBpmBaseFormName} from '../../../_shared/GWTBridge/invoke/bpm/bpmMain';
import {getFormPathArray} from '../../EmailEditor/utils/getFormPathArray';
import {GetMessageColumns} from '../../EmailEditor/services';
import {RuleType} from '../enums';

type AlertChooseParams = {
    Session: string;
    Field: number;
    Pattern?: string;
    IgnoreCase?: boolean;
    FromStart?: boolean;
    Page?: number;
};

const handleRulesUpdateError = (session: string, onCriticalError: () => void) => {
    onCriticalError();
    formsStore.deleteForm(session);
    setNFormFatalError();
    handleFormFatalError();
};

export const fetchAdditionalChooses = async (
    params: AlertChooseParams,
    onCriticalError: () => void = noop
) => {
    const res: priority.netitems.form.FieldSelectRs = await callServer(
        wcfclient.AlertChoose,
        new priority.netitems.form.AlertChooseRq({
            Properties: getAdditionalProps(),
            ...params
        }),
        ServerMethods.AlertChooseRq,
        {
            keepFocus: true,
            messageCallbacks: {
                onErrorAccept: () => handleRulesUpdateError(params.Session, onCriticalError)
            }
        }
    );

    return res;
};

export const fetchDateChooses = async (
    Session: string,
    Field: number,
    onCriticalError?: () => void
) => {
    let cachedRs = formsStore.cachedData.getData('common', 'dateChooses');

    if (!cachedRs) {
        const res = await fetchAdditionalChooses(
            {
                Session,
                Field
            },
            onCriticalError
        );

        if (res.Values.length > 0 && res.Messages.length === 0) {
            formsStore.cachedData.setData('common', 'dateChooses', res);
            cachedRs = res;
        }
    }

    return cachedRs;
};

export const fetchAutomationRulesList = async (
    Session: string,
    Type: priority.netitems.form.RuleType
) => {
    const res: priority.netitems.form.AlertListRs = await callServer(
        wcfclient.AlertList,
        new priority.netitems.form.AlertListRq({
            Properties: getAdditionalProps(),
            Session,
            Type
        }),
        ServerMethods.AlertListRq
    );
    return res?.AlertList || [];
};

export const saveAutomationRule = async (
    AlertData: priority.netitems.form.IAlert,
    Type: priority.netitems.form.RuleType,
    Session: string,
    onCriticalError: () => void,
    refreshOldAPI?: boolean
): Promise<priority.netitems.form.IAlert[]> => {
    const res: priority.netitems.form.AlertListRs = await callServer(
        wcfclient.AlertSave,
        new priority.netitems.form.AlertSaveRq({
            Properties: getAdditionalProps(),
            Session,
            AlertData,
            Type
        }),
        ServerMethods.AlertSaveRq,
        {messageCallbacks: {onCriticalError}}
    );
    if (refreshOldAPI) {
        await updateAutomationRules();
    }
    return res?.AlertList || [];
};

export const fetchAlertChooses = async (Session: string) => {
    const res: priority.netitems.form.IAlertChoosesRs = await callServer(
        wcfclient.AlertChooses,
        new priority.netitems.form.AlertChoosesRq({
            Properties: getAdditionalProps(),
            Session
        }),
        ServerMethods.AlertChooseRq
    );
    return res;
};

export const deleteAutomationRule = async (
    Session: string,
    Alert: number,
    onCriticalError: () => void,
    refreshOldAPI?: boolean
): Promise<boolean> => {
    const response: priority.netitems.form.IEmptyRs = await callServer(
        wcfclient.AlertDelete,
        new priority.netitems.form.AlertDeleteRq({
            Properties: getAdditionalProps(),
            Session,
            Alert
        }),
        ServerMethods.AlertDeleteRq,
        {messageCallbacks: {onCriticalError}}
    );

    if (refreshOldAPI) {
        updateAutomationRules();
    }
    return response.Messages.length === 0;
};

/** Grpc2Net AlertOrder */
export const setAutomationRuleOrder = async (AlertOrder: priority.netitems.form.IAlertOrder[]) => {
    const response: priority.netitems.form.IEmptyRs = await callServer(
        wcfclient.AlertOrder,
        new priority.netitems.form.AlertOrderRq({
            Properties: getAdditionalProps(),
            AlertsOrder: AlertOrder
        }),
        ServerMethods.AlertOrderRq
    );

    return response;
};

export const checkAlertExpression = async (
    Session: string,
    Field: number,
    Expression: string,
    onCriticalError: () => void
) => {
    const response: priority.netitems.form.IAlertCheckExprRs = await callServer(
        wcfclient.AlertCheckExpr,
        new priority.netitems.form.AlertCheckExprRq({
            Properties: getAdditionalProps(),
            Field,
            Expression,
            Session
        }),
        ServerMethods.AlertCheckExprRq,
        {messageCallbacks: {onCriticalError}}
    );

    return response;
};

export const getSearchTypeSystemConst = async (): Promise<number> => {
    if (!formsStore.sysConstants) {
        await setFormSystemConstants();
    }

    const systemConstants = formsStore.sysConstants;

    return (systemConstants?.searchtype?.Int as number) || 0;
};

export const getCoverLetter = async (AlertNumber: number, ruleType: RuleType) => {
    if (ruleType === RuleType.bpm) {
        // BPM rules have cover letters by default in the alert object, so we don't need to fetch them
        return {};
    }
    const response: priority.netitems.nrule.IGetCoverLettersRs = await callServer(
        wcfclient.GetCoverLetters,
        new priority.netitems.nrule.GetCoverLettersRq({
            Properties: getAdditionalProps(),
            AlertNumber
        }),
        ServerMethods.GetCoverLettersRq
    );

    return response;
};

export const fetchEditorMessageColumns = async () => {
    const formPathArray = isCurrentEntBPM()
        ? [getBpmBaseFormName()]
        : getFormPathArray(formsStore.getActiveForm());

    return await GetMessageColumns(formPathArray);
};
